import { GoogleGenAI } from "@google/genai"
// Function to add publishDate if it doesn't exist and sort by publishDate

export const addPublishDateAndSort = (items) => {
  const currentDate = new Date().toISOString() // Get the current date in ISO format

  // Add publishDate if missing
  items.forEach((item) => {
    if (!item.publishDate) {
      item.publishDate = currentDate // Add the publishDate with the current date
    }
  })

  // Sort the items by publishDate in descending order
  items.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))

  return items
}

export function paginateArray(arr, currentPage, itemsPerPage) {
  // Calculate the starting index of the items for the current page
  const startIndex = (currentPage - 1) * itemsPerPage

  // Calculate the ending index of the items for the current page
  const endIndex = startIndex + itemsPerPage

  // Return the sliced array of items for the current page
  return arr.slice(startIndex, endIndex)
}

export const formatPrice = (price) => {
  return `$${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
}

export function formatKilo(km) {
  if (km >= 1000) {
    // If the value is in the millions
    return (km / 1000).toFixed(1) + "M km"
  }
  return km + " km"
}

const normalizeImagePath = (src) => {
  if (src.startsWith("http://") || src.startsWith("https://")) {
    return src // already a full URL
  }
  return `/cars/${src}` // relative path
}

export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    // If image loading is successful
    img.onload = () => {
      resolve(img) // Resolving with the image object
    }

    // If image loading fails
    img.onerror = (error) => {
      reject(new Error(`Failed to load image at ${src}: ${error.message}`)) // Reject with a descriptive error
    }

    img.src = normalizeImagePath(src)
  })
}

export function generateWhatsappLink(phoneNumber, message) {
  /**
   * Generates a WhatsApp link from a phone number and a message.
   *
   * @param phoneNumber - The phone number (in international format without spaces or special characters).
   * @param message - The message to send (will be URL encoded).
   * @returns The WhatsApp link with the message.
   */
  const baseUrl = "https://wa.me/"
  const encodedMessage = encodeURIComponent(message) // URL encode the message
  return `${baseUrl}${phoneNumber}?text=${encodedMessage}`
}

export function today() {
  let today = new Date()
  let dd = today.getDate()
  let mm = today.getMonth() + 1 //January is 0!

  let yyyy = today.getFullYear()
  if (dd < 10) {
    dd = "0" + dd
  }
  if (mm < 10) {
    mm = "0" + mm
  }
  return (today = dd + "-" + mm + "-" + yyyy)
}

export async function generateDescription(carValues) {
  const ai = new GoogleGenAI({
    apiKey: import.meta.env.VITE_GEMINI,
  })
  const config = {
    responseMimeType: "application/json",
  }
  const model = "gemini-2.5-pro-exp-03-25"
  const contents = [
    {
      role: "user",
      parts: [
        {
          text: `Generate a description like this: "BMW 118i 2021 - 40,000 Km | 110,000,000 precio negociable:\\n\\n Vehículo en excelente estado, con mantenimiento y cuidados al día único dueño. Ideal para quienes buscan un automóvil de alta gama, confiable y con gran desempeño.\\n\\nCaracterísticas:\\n   - Motor eficiente y potente con tecnología de última generación motor 3 cilindros BMW TwinPower Turbo.\\n   - Transmisión automática que asegura un manejo suave y preciso.\\n   - Diseño elegante y moderno con acabados premium.\\n\\nEste BMW 118i es la opción perfecta para quienes buscan lujo, confort y rendimiento a un excelente precio."
          for the following car with the following features
          Marca: ${carValues.marca}
          Modelo: ${carValues.modelo}
          Año: ${carValues.año}
          Kilometraje: ${carValues.kilometros}
          Carrocería: ${carValues.carroceria}
          Motor: ${carValues.motor}
          Transmisión: ${carValues.transmision}
          Precio: ${carValues.precio}`,
        },
      ],
    },
    {
      role: "model",
      parts: [
        {
          text: `The user wants a car description following a specific template.
The template includes:
- Title: \`[Marca] [Modelo] [Año] - [Kilometraje] Km | [Precio]\` (Price is not provided, so I'll omit that part or add a placeholder)
- Body:
    - General condition and ownership status.
    - Target audience.
    - Features list:
        - Engine details.
        - Transmission details.
        - Design/Body type details.
    - Concluding sentence summarizing the car's appeal.

Let's break down the information using an example:
- Marca: Toyota
- Modelo: Corolla
- Año: 2009
- Kilometraje: 12000
- Carrocería: Suv (This seems unusual for a Corolla, but I'll use it as provided)
- Motor: 1.8 hibrido
- Transmisión: automatica
- Precio: Not provided. I will add "precio a consultar" or similar.

Now, let's adapt the template:

**Title:** Toyota Corolla 2009 - 12,000 Km | Precio a consultar:

**Body:**
- General condition: Assuming good condition due to low mileage. Will mention "excelente estado" and "bajo kilometraje". Ownership status not provided, will omit "único dueño".
- Target audience: People looking for a reliable, efficient SUV (even if it's a Corolla).
- Features:
    - Motor: 1.8 híbrido, eficiente y confiable.
    - Transmisión: Automática para un manejo cómodo.
    - Carrocería/Diseño: Tipo SUV (as provided), práctico y versátil.
- Conclusion: Perfect option for reliability, efficiency, and practicality.

Let's draft the description:

"Toyota Corolla 2009 - 12,000 Km | Precio a consultar:\\n\\n Vehículo en excelente estado y con muy bajo kilometraje. Ideal para quienes buscan un automóvil confiable, económico y versátil para el día a día.\\n\\nCaracterísticas:\\n   - Motor 1.8 híbrido, reconocido por su eficiencia en consumo y bajas emisiones.\\n   - Transmisión automática que proporciona una conducción suave y sencilla.\\n   - Carrocería tipo SUV (según información proporcionada), ofreciendo practicidad y buen espacio.\\n\\nEste Toyota Corolla es una opción inteligente para quienes valoran la durabilidad, el bajo consumo y la comodidad a un precio accesible."

Now, I need to format this as a JSON string value within a JSON object. The key should probably be "description".`,
        },
        {
          text: `{
  "description": "Toyota Corolla 2009 - 12,000 Km | Precio a consultar:\\n\\n Vehículo en excelente estado y con muy bajo kilometraje. Ideal para quienes buscan un automóvil confiable, económico y versátil.\\n\\nCaracterísticas:\\n   - Motor 1.8 híbrido, eficiente y respetuoso con el medio ambiente.\\n   - Transmisión automática que ofrece una conducción cómoda y relajada.\\n   - Carrocería tipo SUV (según lo indicado), práctica para diversas necesidades.\\n\\nEste Toyota Corolla es una excelente opción por su fiabilidad, bajo consumo y versatilidad."
}`,
        },
      ],
    },
    {
      role: "user",
      parts: [
        {
          text: `INSERT_INPUT_HERE`,
        },
      ],
    },
  ]

  const response = await ai.models.generateContentStream({
    model,
    config,
    contents,
  })

  let fullResponse = ""

  for await (const chunk of response) {
    if (chunk?.candidates[0].content.parts[0].text) {
      fullResponse += chunk?.candidates[0].content.parts[0].text
    }
  }

  // Parse the JSON string if necessary (if response is like `{"description": "...text..."}`)
  try {
    const parsed = JSON.parse(fullResponse)
    return parsed.description ?? fullResponse
  } catch (err) {
    console.warn("Failed to parse response as JSON. Returning raw text.", err)
    return fullResponse
  }
}

// Extract the file ID from the original URL
// And convert it to the direct viewable format

export function convertDriveUrl(fileIdOrUrl, width = 1000, height = 1000) {
  let fileId = fileIdOrUrl

  // If a full URL is passed, extract the file ID using a regex
  const match = fileIdOrUrl.match(/(?:id=|\/d\/)([a-zA-Z0-9_-]{25,})/)
  if (match && match[1]) {
    fileId = match[1]
  }

  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${width}-h${height}`
  //&sz=w${width}-h${height}
}

export function extractDriveFileId(url) {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)\//)
  return match ? match[1] : null
}

export function removeCountryCode(phone) {
  return phone.startsWith("+57") ? phone.slice(3) : phone
}

export function resizeImage(file, maxWidth = 800, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.src = reader.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Canvas context not available");

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject("Image compression failed");
          const resizedFile = new File([blob], file.name, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}


export function daysSince(dateString) {
  const inputDate = new Date(dateString);
  const today = new Date();

  // Clear time parts to calculate full days only
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfInput = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffMs = startOfToday - startOfInput;
  
  return Math.floor(diffMs / msPerDay);
}
